import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Palette, DollarSign, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatToEST, formatCurrency } from '@/utils/dateUtils';
import { getFinalSalePrice } from '@/utils/priceUtils';

interface ArtworkDetails {
  id: string;
  title: string;
  image_url: string;
  additional_images: string[];
  medium: string;
  dimensions: string;
  frame_dimensions: string;
  year: number;
  description: string;
  estimate_low: number;
  estimate_high: number;
  artist: {
    name: string;
  };
}

interface LotDetails {
  id: string;
  lot_number: number;
  starting_bid: number;
  current_bid: number;
  reserve_price: number;
  status: string;
  artworks: ArtworkDetails;
}

interface AuctionWithLots {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: string;
  location: string;
  image_url: string;
  lots: LotDetails[];
}

export default function AuctionLotsCarousel() {
  const [selectedLot, setSelectedLot] = useState<LotDetails | null>(null);
  const [selectedAuction, setSelectedAuction] = useState<AuctionWithLots | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const stepRef = useRef<number>(0);

  const { data: auctionsWithLots, isLoading } = useQuery({
    queryKey: ['mostRecentCompletedAuction'],
    queryFn: async () => {
      console.log('🎯 Fetching most recent completed auction...');
      
      // Get the most recent completed auction
      const { data: auctions, error: auctionsError } = await supabase
        .from('auctions')
        .select('id, title, description, start_date, end_date, status, location, image_url')
        .eq('status', 'completed' as any)
        .order('end_date', { ascending: false })
        .limit(1);

      if (auctionsError || !auctions || auctions.length === 0) {
        console.log('❌ No completed auctions found');
        return [];
      }

      const auction = auctions[0];
      console.log(`✅ Found most recent auction: "${(auction as any).title}"`);
      
      // Get all lots from this auction
      const { data: lots, error: lotsError } = await supabase
        .from('lots')
        .select(`
          id,
          lot_number,
          starting_bid,
          current_bid,
          reserve_price,
          status,
          lot_prices (
            price
          ),
          artworks (
            title,
            image_url,
            additional_images,
            medium,
            dimensions,
            frame_dimensions,
            year,
            description,
            estimate_low,
            estimate_high,
            artist:artist_id (
              name
            )
          )
        `)
        .eq('auction_id', (auction as any).id)
        .order('lot_number');

      if (lotsError) {
        console.error('Error fetching lots:', lotsError);
        return [];
      }

      const validLots = lots || [];
      
      // Calculate statistics
      const soldLots = validLots.filter((lot: any) => lot.status === 'sold');
      const passedLots = validLots.filter((lot: any) => lot.status === 'passed');
      const totalSales = soldLots.reduce((sum: number, lot: any) => {
        const finalPrice = lot.lot_prices?.[0]?.price || lot.current_bid || 0;
        return sum + finalPrice;
      }, 0);
      
      console.log(`📦 Auction Statistics:`);
      console.log(`   Total Lots: ${validLots.length}`);
      console.log(`   Lots Sold: ${soldLots.length}`);
      console.log(`   Lots Passed: ${passedLots.length}`);
      console.log(`   Total Sales: $${totalSales.toLocaleString()}`);

      return [{
        ...(auction as any),
        lots: validLots,
        statistics: {
          totalLots: validLots.length,
          lotsSold: soldLots.length,
          lotsPassed: passedLots.length,
          totalSales: totalSales
        }
      }];
    },
  });

  // Memoize lots processing to prevent unnecessary re-renders
  const processedLots = useMemo(() => {
    if (!auctionsWithLots || auctionsWithLots.length === 0) return [];
    
    const allLots = auctionsWithLots.flatMap(auction => 
      auction.lots
        .filter(lot => lot.artworks && lot.artworks.image_url) // Filter for lots with artwork and images
        .map(lot => ({
          ...lot,
          auctionTitle: auction.title,
          auctionStatus: auction.status,
          auctionDate: auction.start_date,
          auctionId: auction.id
        }))
    );

    // Show ALL lots from the most recent auction (no limit)
    return allLots;
  }, [auctionsWithLots]);

  // Optimized handlers with useCallback to prevent re-renders
  const handleLotClick = useCallback((lot: any) => {
    setSelectedLot(lot);
    setCurrentImageIndex(0);
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
    const auction = auctionsWithLots?.find(a => a.id === lot.auctionId);
    setSelectedAuction(auction || null);
  }, [auctionsWithLots]);

  const getAllImages = useCallback((lot: LotDetails) => {
    const images = [lot.artworks.image_url];
    if (lot.artworks.additional_images && lot.artworks.additional_images.length > 0) {
      images.push(...lot.artworks.additional_images);
    }
    return images;
  }, []);

  const handleImageNavigation = useCallback((direction: 'prev' | 'next') => {
    if (!selectedLot) return;
    const images = getAllImages(selectedLot);
    if (direction === 'prev') {
      setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    } else {
      setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }
    // Reset image position and scale when changing images
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  }, [selectedLot, getAllImages]);

  // Enhanced image pan functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  }, [imageScale, imagePosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, imageScale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newScale = Math.min(Math.max(1, imageScale + delta), 3);
    setImageScale(newScale);
    
    if (newScale === 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  }, [imageScale]);

  const getSoldPassedAmount = useCallback((lot: any) => {
    // Use the utility function to handle price retrieval from multiple sources
    return getFinalSalePrice(lot);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const container = carouselContainerRef.current;
    if (!container) {
      return;
    }

    const updateButtons = () => {
      const node = carouselContainerRef.current;
      if (!node) return;
      setCanScrollPrev(node.scrollLeft > 0);
      setCanScrollNext(node.scrollLeft + node.clientWidth < node.scrollWidth - 1);
    };

    const measureStep = () => {
      const node = carouselContainerRef.current;
      if (!node) return;
      const firstItem = node.querySelector<HTMLElement>('[data-carousel-item]');
      if (!firstItem) return;
      const containerStyle = window.getComputedStyle(node);
      const gap = parseFloat(containerStyle.columnGap || '0');
      stepRef.current = firstItem.offsetWidth + gap;
    };

    const handleResize = () => {
      measureStep();
      updateButtons();
    };

    measureStep();
    updateButtons();

    container.addEventListener('scroll', updateButtons);
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', updateButtons);
      window.removeEventListener('resize', handleResize);
    };
  }, [processedLots]);

  const handleScrollBy = useCallback((direction: 'prev' | 'next') => {
    const node = carouselContainerRef.current;
    if (!node) return;
    const step = stepRef.current || node.clientWidth;
    const delta = direction === 'prev' ? -step : step;
    node.scrollBy({ left: delta, behavior: 'auto' });
  }, []);

  if (isLoading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded-md w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded-md w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (processedLots.length === 0) {
    return (
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="container mx-auto px-4 relative z-10">
          {/* Professional Header Section */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center justify-center px-8 py-3 mb-8 bg-white/5 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
              <Palette className="h-5 w-5 mr-3 text-emerald-400" />
              <span className="text-white font-light tracking-wide">Our Past Auction</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 text-white tracking-tight leading-tight">
              <span className="block">Discover Florida</span>
              <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-blue-400 bg-clip-text text-transparent font-light">Highwaymen Art</span>
            </h2>
            
            <p className="text-2xl text-white/70 max-w-5xl mx-auto leading-relaxed font-light">
              Explore our most recent completed auction featuring <span className="text-emerald-300 font-normal">Florida Highwaymen artwork</span> from legendary artists
            </p>
            
            {/* Auction Statistics for empty state */}
            {auctionsWithLots && auctionsWithLots[0]?.statistics && (
              <div className="mt-12 flex flex-wrap justify-center gap-8">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl px-8 py-6 border border-white/10">
                  <div className="text-4xl font-light text-emerald-400 mb-2">
                    {auctionsWithLots[0].statistics.lotsSold}
                  </div>
                  <div className="text-white/60 text-sm uppercase tracking-wider">Lots Sold</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl px-8 py-6 border border-white/10">
                  <div className="text-4xl font-light text-emerald-400 mb-2">
                    ${auctionsWithLots[0].statistics.totalSales.toLocaleString()}
                  </div>
                  <div className="text-white/60 text-sm uppercase tracking-wider">Total Sales</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl px-8 py-6 border border-white/10">
                  <div className="text-4xl font-light text-white/50 mb-2">
                    {auctionsWithLots[0].statistics.lotsPassed}
                  </div>
                  <div className="text-white/60 text-sm uppercase tracking-wider">Lots Passed</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Sophisticated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/10 to-transparent" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Auction Header with Title & Date */}
          {auctionsWithLots && auctionsWithLots[0] && (
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center justify-center px-6 py-2.5 mb-6 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-blue-500/10 backdrop-blur-xl rounded-full border border-white/10 shadow-lg">
                <Palette className="h-4 w-4 mr-2.5 text-emerald-400" />
                <span className="text-emerald-300 font-light tracking-wide text-sm uppercase">Most Recent Auction</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 text-white tracking-tight">
                {auctionsWithLots[0].title}
              </h2>
              
              <div className="flex items-center justify-center gap-6 text-white/60 text-sm md:text-base mb-8">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-emerald-400" />
                  <span className="font-light">{formatToEST(auctionsWithLots[0].start_date, 'MMMM d, yyyy')}</span>
                </div>
                <div className="h-4 w-px bg-white/20"></div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-teal-400" />
                  <span className="font-light">{auctionsWithLots[0].statistics?.totalLots || 0} Lots</span>
                </div>
              </div>

              {/* Enhanced Statistics Cards */}
              {auctionsWithLots[0]?.statistics && (
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                    <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/10 hover:border-emerald-500/30 transition-all duration-300">
                      <div className="text-3xl md:text-4xl font-light text-emerald-400 mb-1">
                        {auctionsWithLots[0].statistics.lotsSold}
                      </div>
                      <div className="text-white/60 text-xs uppercase tracking-wider font-light">Lots Sold</div>
                    </div>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                    <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/10 hover:border-teal-500/30 transition-all duration-300">
                      <div className="text-3xl md:text-4xl font-light text-teal-400 mb-1">
                        {formatCurrency(auctionsWithLots[0].statistics.totalSales)}
                      </div>
                      <div className="text-white/60 text-xs uppercase tracking-wider font-light">Total Sales</div>
                    </div>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                    <div className="relative bg-white/5 backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/10 hover:border-blue-500/30 transition-all duration-300">
                      <div className="text-3xl md:text-4xl font-light text-white/50 mb-1">
                        {auctionsWithLots[0].statistics.lotsPassed}
                      </div>
                      <div className="text-white/60 text-xs uppercase tracking-wider font-light">Lots Passed</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhanced Carousel with Navigation */}
          <div className="relative">
            {/* Navigation Buttons */}
            {canScrollPrev && (
              <button
                onClick={() => handleScrollBy('prev')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white p-3 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 border border-white/20 hidden md:flex items-center justify-center"
                aria-label="Previous lots"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            
            {canScrollNext && (
              <button
                onClick={() => handleScrollBy('next')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white p-3 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 border border-white/20 hidden md:flex items-center justify-center"
                aria-label="Next lots"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}

            {/* Carousel Container */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex gap-6 pb-4 overflow-x-auto scroll-smooth snap-x snap-mandatory"
                ref={carouselContainerRef}
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(16, 185, 129, 0.3) transparent' }}
              >
                {processedLots.map((lot) => (
                  <div
                    key={lot.id}
                    className="flex-shrink-0 w-[320px] sm:w-[360px] snap-start cursor-pointer group"
                    data-carousel-item
                    onClick={() => handleLotClick(lot)}
                  >
                    <div className="bg-gradient-to-br from-white/5 via-white/3 to-transparent backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl border border-white/10 h-full transition-all duration-300 hover:border-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/10 hover:scale-[1.02]">
                      {/* Image Section */}
                      <div className="relative h-72 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
                        <img 
                          src={lot.artworks.image_url} 
                          alt={lot.artworks.title}
                          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                        {/* Badges */}
                        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                          <Badge className="bg-gradient-to-r from-emerald-500/90 to-teal-500/90 backdrop-blur-sm text-white border-0 font-light shadow-lg px-3 py-1">
                            Lot #{lot.lot_number}
                          </Badge>
                          <Badge 
                            className={
                              lot.status === 'sold' 
                                ? 'bg-green-500/90 text-white border-0 shadow-lg'
                              : lot.status === 'passed' 
                                ? 'bg-amber-500/90 text-white border-0 shadow-lg'
                              : 'bg-blue-500/90 text-white border-0 shadow-lg'
                            }
                          >
                            {lot.status?.toUpperCase() || 'ACTIVE'}
                          </Badge>
                        </div>

                        {/* Artist Name Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-slate-950/90 to-transparent">
                          <p className="text-white/90 font-light text-sm tracking-wide">
                            {lot.artworks.artist.name}
                          </p>
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="p-6">
                        {/* Sale Status */}
                        {lot.status === 'sold' && getSoldPassedAmount(lot) && (
                          <div className="mb-3 px-3 py-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg border border-emerald-500/20">
                            <p className="text-emerald-400 font-light text-sm tracking-wide">
                              SOLD for {formatCurrency(getSoldPassedAmount(lot))}
                            </p>
                          </div>
                        )}

                        {lot.status === 'passed' && (
                          <div className="mb-3 px-3 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                            <p className="text-amber-400 font-light text-sm tracking-wide">
                              PASSED
                            </p>
                          </div>
                        )}

                        {/* Title */}
                        <h3 className="text-xl font-light text-white mb-2 leading-tight tracking-wide line-clamp-2 group-hover:text-emerald-300 transition-colors duration-300">
                          {lot.artworks?.title || 'Untitled'}
                        </h3>
                        
                        {/* Artist */}
                        <p className="text-teal-400 font-light text-base mb-3 tracking-wide">
                          by {lot.artworks?.artist?.name || 'Unknown Artist'}
                        </p>
                        
                        {/* Details */}
                        <div className="space-y-1 text-white/60 text-sm font-light mb-4">
                          {lot.artworks?.medium && (
                            <p className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/50"></span>
                              {lot.artworks.medium}
                            </p>
                          )}
                          {lot.artworks?.year && (
                            <p className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-500/50"></span>
                              {lot.artworks.year}
                            </p>
                          )}
                          {lot.artworks?.dimensions && (
                            <p className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500/50"></span>
                              {lot.artworks.dimensions}
                            </p>
                          )}
                        </div>
                        
                        {/* Current Bid (for active lots) */}
                        {lot.status !== 'sold' && lot.status !== 'passed' && (
                          <div className="flex items-center justify-between pt-4 border-t border-white/10">
                            <div className="text-sm text-white/50 font-light">
                              Starting Bid
                            </div>
                            <div className="text-lg font-light text-emerald-400 tracking-wide">
                              {formatCurrency(lot.current_bid || lot.starting_bid || 0)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="mt-6 flex md:hidden justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleScrollBy('prev')}
                disabled={!canScrollPrev}
                className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-emerald-500/50 disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleScrollBy('next')}
                disabled={!canScrollNext}
                className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-emerald-500/50 disabled:opacity-30"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* Lot Counter */}
            <div className="mt-8 text-center">
              <p className="text-white/40 text-sm font-light">
                Showing all {processedLots.length} lots from this auction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Modal with Pan Functionality */}
      <Dialog open={!!selectedLot} onOpenChange={() => setSelectedLot(null)}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden p-0 bg-slate-950/95 backdrop-blur-xl border border-white/10">
          {selectedLot && selectedAuction && (
            <>
              <DialogHeader className="p-6 pb-4">
                <DialogTitle className="text-3xl font-light text-white pr-8 tracking-wide">
                  {selectedLot.artworks.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid md:grid-cols-2 gap-6 p-6 pt-0 overflow-y-auto max-h-[80vh]">
                {/* Enhanced Image Section with Pan */}
                <div className="space-y-4">
                  <div className="relative group">
                    <div 
                      className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-grab active:cursor-grabbing"
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onWheel={handleWheel}
                    >
                      <img 
                        ref={imageRef}
                        src={getAllImages(selectedLot)[currentImageIndex]} 
                        alt={selectedLot.artworks.title}
                        className="w-full h-full object-contain select-none"
                        style={{ 
                          transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                          transformOrigin: 'center center'
                        }}
                        draggable={false}
                      />

                      {/* Image navigation arrows */}
                      {getAllImages(selectedLot).length > 1 && (
                        <>
                          <button
                            onClick={() => handleImageNavigation('prev')}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 backdrop-blur-sm rounded-full p-2 text-white z-10"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleImageNavigation('next')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 backdrop-blur-sm rounded-full p-2 text-white z-10"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </>
                      )}

                      {/* Zoom indicator */}
                      {imageScale > 1 && (
                        <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm">
                          {Math.round(imageScale * 100)}%
                        </div>
                      )}
                    </div>
                    
                    {/* Image instructions and counter */}
                    <div className="text-center mt-2 space-y-1">
                      {getAllImages(selectedLot).length > 1 && (
                        <span className="text-sm text-white/60 font-light">
                          {currentImageIndex + 1} of {getAllImages(selectedLot).length}
                        </span>
                      )}
                      <p className="text-xs text-white/50 font-light">
                        Scroll to zoom • Drag to pan when zoomed
                      </p>
                    </div>
                  </div>
                  
                  {/* Thumbnail navigation */}
                  {getAllImages(selectedLot).length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {getAllImages(selectedLot).map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            index === currentImageIndex 
                              ? 'border-emerald-400 shadow-lg' 
                              : 'border-white/20'
                          }`}
                        >
                          <img 
                            src={img} 
                            alt={`View ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Artwork Details */}
                <div className="space-y-6">
                  {/* Artist Info */}
                  <div>
                    <h3 className="text-2xl font-light text-emerald-400 mb-3 tracking-wide">
                      by {selectedLot.artworks?.artist?.name || 'Unknown Artist'}
                    </h3>
                  </div>

                  {/* Artwork Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
                      <p className="text-white/60 text-sm font-light">Medium</p>
                      <p className="font-light text-white tracking-wide">{selectedLot.artworks.medium || 'Not specified'}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
                      <p className="text-white/60 text-sm font-light">Frame Dimensions</p>
                      <p className="font-light text-white tracking-wide">{selectedLot.artworks.frame_dimensions || 'Not specified'}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
                      <p className="text-white/60 text-sm font-light">Painting Size</p>
                      <p className="font-light text-white tracking-wide">{selectedLot.artworks.dimensions || 'Not specified'}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
                      <p className="text-white/60 text-sm font-light">Lot Number</p>
                      <p className="font-light text-white tracking-wide">#{selectedLot.lot_number}</p>
                    </div>
                  </div>

                  {/* Additional Useful Information */}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLot.reserve_price && (
                      <div className="bg-purple-500/20 backdrop-blur-xl rounded-xl p-4 border border-purple-400/30">
                        <p className="text-purple-300 text-sm font-light">Reserve Price</p>
                        <p className="font-light text-white text-lg tracking-wide">
                          {formatCurrency(selectedLot.reserve_price)}
                        </p>
                      </div>
                    )}
                    {selectedLot.artworks?.estimate_low && selectedLot.artworks?.estimate_high && (
                      <div className="bg-blue-500/20 backdrop-blur-xl rounded-xl p-4 border border-blue-400/30">
                        <p className="text-blue-300 text-sm font-light">Pre-Sale Estimate</p>
                        <p className="font-light text-white text-lg tracking-wide">
                          {formatCurrency(selectedLot.artworks.estimate_low)} - {formatCurrency(selectedLot.artworks.estimate_high)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Auction and Status Info */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                    <h4 className="font-light text-white mb-4 text-lg tracking-wide">
                      Auction & Status Information
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-emerald-400" />
                        <span className="font-light text-white/90">{selectedAuction.title}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-blue-400" />
                        <span className="font-light text-white/90">{formatToEST(selectedAuction.start_date)}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-5 w-5 text-teal-400" />
                        {selectedLot.status === 'sold' || selectedLot.status === 'passed' || selectedLot.status === 'withdrawn' ? (
                          <div>
                            <span className={`font-light uppercase text-lg tracking-wide ${
                              selectedLot.status === 'sold' ? 'text-emerald-400' :
                              selectedLot.status === 'passed' ? 'text-red-400' :
                              'text-white/60'
                            }`}>
                              {selectedLot.status}
                            </span>
                            {selectedLot.status === 'sold' && getSoldPassedAmount(selectedLot) && (
                              <span className="ml-2 text-white/80 font-light">
                                for {formatCurrency(getSoldPassedAmount(selectedLot))}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="font-light text-white/90">Current Bid: {formatCurrency(selectedLot.current_bid || selectedLot.starting_bid)}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {selectedLot.artworks.description && (
                    <div>
                      <h4 className="font-light text-white mb-3 text-lg tracking-wide">
                        Description
                      </h4>
                      <p className="text-white/70 leading-relaxed font-light">
                        {selectedLot.artworks.description}
                      </p>
                    </div>
                  )}


                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </>
  );
} 